import { useMemo, type SyntheticEvent } from "react";

import type { components } from "../../api/schema";
import { EMPTY_METRIC_FILTERS } from "../../lib/dashboardData";
import { docUrl } from "../../lib/docLinks";
import {
  METRIC_METADATA,
  METRIC_OPTIONS,
  type MetricFilters,
} from "../../lib/dashboardWidgets";
import type { MetricName } from "../../lib/dashboardMetrics";
import {
  Button,
  Grid,
  HelpTip,
  Inline,
  InlineError,
  MultiSelect,
  PageHeader,
  Section,
  Select,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import {
  AlertEntitySelect,
  type AlertRuleEntityOptions,
} from "./AlertEntitySelect";
import { AlertRuleBacktest } from "./AlertRuleBacktest";
import { AlertRuleMessageEditor } from "./AlertRuleMessageEditor";
import { alertChannelOptionsForSelection } from "./alertChannelSelection";
import { ALERT_RULE_FILTER_FIELDS } from "./alertRuleFields";
import { useAlertRuleDraft } from "./useAlertRuleDraft";

type AlertChannel = components["schemas"]["AlertChannelResponse"];
type AlertComparator = components["schemas"]["AlertComparator"];
type AlertMessageCatalog = components["schemas"]["AlertMessageCatalogResponse"];
type AlertNoDataPolicy = components["schemas"]["AlertNoDataPolicy"];
type AlertRuleCreate = components["schemas"]["AlertRuleCreate"];
type AlertRule = components["schemas"]["AlertRuleResponse"];
type AlertRulePreviewRequest = components["schemas"]["AlertRulePreviewRequest"];
type AlertRulePreviewResponse =
  components["schemas"]["AlertRulePreviewResponse"];
type AlertSeverity = components["schemas"]["AlertSeverity"];
type AlertTrigger = AlertRuleCreate["trigger"];
type AlertTriggerKind = components["schemas"]["AlertTriggerKind"];

export interface AlertRuleAuthoringFormProps {
  /** Routes own header chrome; drawers own a fixed foot row (UI-LAW §2.12). */
  actionsPlacement?: "header" | "footer";
  agentId?: string;
  catalog?: AlertMessageCatalog;
  channels: AlertChannel[];
  entityOptions?: AlertRuleEntityOptions;
  errorMessage?: string;
  isOptionsPending: boolean;
  isPending: boolean;
  isPreviewPending: boolean;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onPreview?: (
    body: AlertRulePreviewRequest,
  ) => Promise<AlertRulePreviewResponse>;
  onRetryOptions?: () => void;
  onSubmit: (body: AlertRuleCreate) => Promise<void>;
  optionsErrorMessage?: string;
  preview?: AlertRulePreviewResponse;
  previewErrorMessage?: string;
  rule?: AlertRule;
  submitLabel?: string;
}

function routesFor(
  when: "firing" | "resolved",
  channelIds: string[],
): AlertRuleCreate["routes"] {
  return channelIds.length > 0 ? [{ channel_ids: channelIds, when }] : [];
}

function timeRangeForRecentHistory(): components["schemas"]["MetricTimeRange"] {
  const end = new Date();
  // S10 replays at live cadence and rejects ranges beyond the shipped
  // 200-evaluation bound. Three hours fits the shipped 60-second cadence
  // without silently widening or truncating the preview.
  const start = new Date(end.getTime() - 3 * 60 * 60 * 1000);
  return { end: end.toISOString(), start: start.toISOString() };
}

export function AlertRuleAuthoringForm({
  actionsPlacement = "footer",
  agentId,
  catalog,
  channels,
  entityOptions = {
    agents: [],
    agentVersions: [],
    batches: [],
    markers: [],
    templates: [],
  },
  errorMessage,
  isOptionsPending,
  isPending,
  isPreviewPending,
  onCancel,
  onDirtyChange,
  onPreview,
  onRetryOptions,
  onSubmit,
  optionsErrorMessage,
  preview,
  previewErrorMessage,
  rule,
  submitLabel = "Create rule",
}: AlertRuleAuthoringFormProps) {
  // ASSUMED(description stays omitted until AlertRuleCreate exposes a field.)
  const {
    comparator,
    consecutiveBreaches,
    displayName,
    enabled,
    eventAgentId,
    eventTarget,
    firingChannelIds,
    gateComparator,
    gateEnabled,
    gateThreshold,
    measure,
    messageConfig,
    metric,
    metricFilters,
    minOccurrences,
    noDataPolicy,
    renotifySeconds,
    resolveAfterClear,
    resolvedChannelIds,
    setComparator,
    setConsecutiveBreaches,
    setDisplayName,
    setEnabled,
    setEventAgentId,
    setEventTarget,
    setFiringChannelIds,
    setGateComparator,
    setGateEnabled,
    setGateThreshold,
    setMeasure,
    setMessageConfig,
    setMetric,
    setMetricFilters,
    setMinOccurrences,
    setNoDataPolicy,
    setRenotifySeconds,
    setResolveAfterClear,
    setResolvedChannelIds,
    setSeverity,
    setTemplateId,
    setThreshold,
    setTriggerKind,
    setWindowSeconds,
    severity,
    templateId,
    threshold,
    triggerKind,
    windowSeconds,
  } = useAlertRuleDraft(rule, agentId);
  const selectedChannelIds = useMemo(
    () => [...firingChannelIds, ...resolvedChannelIds],
    [firingChannelIds, resolvedChannelIds],
  );
  const channelOptions = useMemo(() => {
    const knownIds = new Set(channels.map((channel) => channel.id));
    const options = channels.map((channel) => ({
      label: rule
        ? channel.display_name
        : `${channel.display_name} · ${channel.channel_type}`,
      value: channel.id,
    }));
    if (!rule) {
      return options;
    }
    return [
      ...options,
      ...selectedChannelIds
        .filter((channelId) => !knownIds.has(channelId))
        .map((channelId) => ({ label: channelId, value: channelId })),
    ];
  }, [channels, rule, selectedChannelIds]);
  const metricMetadata = METRIC_METADATA[metric];
  const supportedFilters = new Set(metricMetadata.filters);
  const gateSupported = eventTarget === "batch_completed";
  const selectedChannelCount = new Set(selectedChannelIds).size;
  const busy = isPending || isPreviewPending;

  function markDirty() {
    onDirtyChange?.(true);
  }

  function updateFilter(key: keyof MetricFilters, value: string) {
    markDirty();
    setMetricFilters((current) => ({
      ...current,
      [key]: value.length > 0 ? value : null,
    }));
  }

  function buildTrigger(): AlertTrigger {
    if (triggerKind === "event") {
      // scheduled_run_failed has no batch to score, so the domain validator
      // rejects any gate on it. Drop a stale gate here rather than send a 422.
      return {
        gate:
          gateEnabled && gateSupported
            ? {
                comparator: gateComparator,
                measure: "pass_rate",
                threshold: Number(gateThreshold),
              }
            : null,
        kind: "event",
        target: eventTarget,
        scope: {
          agent_id: eventAgentId || null,
          template_id: templateId || null,
        },
      };
    }
    return {
      comparator,
      consecutive_breaches: Number(consecutiveBreaches),
      filters: metricFilters,
      kind: "threshold",
      measure,
      metric,
      min_occurrences: Number(minOccurrences),
      no_data_policy: noDataPolicy,
      renotify_seconds: Number(renotifySeconds),
      resolve_after_clear: Number(resolveAfterClear),
      threshold: Number(threshold),
      window_seconds: Number(windowSeconds),
    };
  }

  async function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const routes = [
      ...routesFor("firing", firingChannelIds),
      ...routesFor("resolved", resolvedChannelIds),
    ];
    await onSubmit({
      display_name: displayName.trim(),
      enabled,
      message_config: messageConfig,
      routes,
      severity,
      trigger: buildTrigger(),
    });
  }

  const errorNotice = errorMessage ? (
    <InlineError>{errorMessage}</InlineError>
  ) : null;
  const actions = (
    <>
      {errorNotice}
      <Button
        disabled={busy}
        onClick={onCancel}
        type="button"
        variant="secondary"
      >
        Cancel
      </Button>
      <Button
        disabled={
          busy ||
          isOptionsPending ||
          optionsErrorMessage != null ||
          selectedChannelCount === 0
        }
        type="submit"
      >
        {isPending ? "Saving..." : submitLabel}
      </Button>
    </>
  );

  return (
    <form
      className={`marker-alert-rule-authoring--${actionsPlacement}`}
      onChange={markDirty}
      onSubmit={(event) => {
        void submit(event).catch(() => undefined);
      }}
    >
      <Stack gap={7}>
        {actionsPlacement === "header" ? (
          <PageHeader actions={actions} compact />
        ) : null}
        <Section heading="Name this alert">
          <Stack gap={4}>
            <TextInput
              disabled={busy}
              label="Name"
              required
              value={displayName}
              onChange={(event) => {
                setDisplayName(event.currentTarget.value);
              }}
            />
            <Grid columns={2} gap={4}>
              <Select
                disabled={busy}
                label="Severity"
                value={severity}
                onChange={(event) => {
                  markDirty();
                  setSeverity(event.currentTarget.value as AlertSeverity);
                }}
              >
                <option value="critical">Critical</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </Select>
              <Select
                disabled={busy}
                label="Status"
                value={enabled ? "enabled" : "disabled"}
                onChange={(event) => {
                  markDirty();
                  setEnabled(event.currentTarget.value === "enabled");
                }}
              >
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </Select>
            </Grid>
          </Stack>
        </Section>

        <Section hairline heading="Choose what to detect">
          <Stack gap={4}>
            <Select
              disabled={busy}
              label="Detection shape"
              value={triggerKind}
              onChange={(event) => {
                markDirty();
                setTriggerKind(event.currentTarget.value as AlertTriggerKind);
              }}
            >
              <option value="threshold">Threshold</option>
              <option value="event">Event</option>
            </Select>

            {triggerKind === "threshold" ? (
              <>
                <Grid columns={2} gap={4}>
                  <Select
                    disabled={busy}
                    label="Metric"
                    value={metric}
                    onChange={(event) => {
                      markDirty();
                      const nextMetric = event.currentTarget
                        .value as MetricName;
                      const nextMetricMetadata = METRIC_METADATA[nextMetric];
                      setMetric(nextMetric);
                      setMeasure(nextMetricMetadata.primaryMeasure);
                      setMetricFilters({
                        ...EMPTY_METRIC_FILTERS,
                        agent_id: nextMetricMetadata.filters.includes(
                          "agent_id",
                        )
                          ? (metricFilters.agent_id ?? agentId ?? null)
                          : null,
                      });
                    }}
                  >
                    {METRIC_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    disabled={busy}
                    label="Measure"
                    value={measure}
                    onChange={(event) => {
                      markDirty();
                      setMeasure(event.currentTarget.value);
                    }}
                  >
                    {metricMetadata.measures.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Grid>
                <Grid columns={2} gap={4}>
                  {ALERT_RULE_FILTER_FIELDS.filter((field) =>
                    supportedFilters.has(field.key),
                  ).map((field) =>
                    field.entity ? (
                      <AlertEntitySelect
                        disabled={busy || isOptionsPending}
                        key={field.key}
                        label={field.label}
                        onChange={(value) => {
                          updateFilter(field.key, value);
                        }}
                        options={entityOptions[field.entity]}
                        value={metricFilters[field.key] ?? ""}
                      />
                    ) : field.options ? (
                      <Select
                        disabled={busy}
                        key={field.key}
                        label={field.label}
                        value={metricFilters[field.key] ?? ""}
                        onChange={(event) => {
                          updateFilter(field.key, event.currentTarget.value);
                        }}
                      >
                        <option value="">All</option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <TextInput
                        disabled={busy}
                        key={field.key}
                        label={field.label}
                        value={metricFilters[field.key] ?? ""}
                        onChange={(event) => {
                          updateFilter(
                            field.key,
                            event.currentTarget.value.trim(),
                          );
                        }}
                      />
                    ),
                  )}
                </Grid>
              </>
            ) : (
              <Grid columns={2} gap={4}>
                <Select
                  disabled={busy}
                  label="Event target"
                  value={eventTarget}
                  onChange={(event) => {
                    markDirty();
                    setEventTarget(
                      event.currentTarget.value as typeof eventTarget,
                    );
                    // Only batch_completed accepts a gate; a gate configured
                    // before the switch must not survive it.
                    setGateEnabled(false);
                  }}
                >
                  <option value="batch_completed">Batch completed</option>
                  <option value="scheduled_run_failed">
                    Scheduled run failed
                  </option>
                </Select>
                <AlertEntitySelect
                  disabled={busy || isOptionsPending}
                  label="Agent scope"
                  onChange={(value) => {
                    markDirty();
                    setEventAgentId(value);
                  }}
                  options={entityOptions.agents}
                  value={eventAgentId}
                />
                <AlertEntitySelect
                  disabled={busy || isOptionsPending}
                  label="Template scope"
                  onChange={(value) => {
                    markDirty();
                    setTemplateId(value);
                  }}
                  options={entityOptions.templates}
                  value={templateId}
                />
              </Grid>
            )}
            {optionsErrorMessage ? (
              <Inline gap={3}>
                <InlineError>{optionsErrorMessage}</InlineError>
                <Button
                  onClick={onRetryOptions}
                  type="button"
                  variant="secondary"
                >
                  Retry options
                </Button>
              </Inline>
            ) : null}
          </Stack>
        </Section>

        <Section
          hairline
          heading="Set the conditions"
          help={
            <HelpTip
              docHref={docUrl("concepts/alert-rules")}
              docLabel="Alert rules"
              label="About alert evaluation cadence"
            >
              {"Firing and recovery use the same evaluation cadence."}
            </HelpTip>
          }
        >
          <Stack gap={4}>
            {triggerKind === "threshold" ? (
              <>
                <Grid columns={2} gap={4}>
                  <Select
                    disabled={busy}
                    label="Comparator"
                    value={comparator}
                    onChange={(event) => {
                      markDirty();
                      setComparator(
                        event.currentTarget.value as AlertComparator,
                      );
                    }}
                  >
                    <option value="gt">Greater than</option>
                    <option value="gte">Greater than or equal</option>
                    <option value="lt">Less than</option>
                    <option value="lte">Less than or equal</option>
                    <option value="eq">Equal</option>
                  </Select>
                  <TextInput
                    disabled={busy}
                    label="Threshold"
                    required
                    step="any"
                    type="number"
                    value={threshold}
                    onChange={(event) => {
                      setThreshold(event.currentTarget.value);
                    }}
                  />
                </Grid>
                <Grid columns={2} gap={4}>
                  {/* ASSUMED(source-data cadence is the only server signal
                  available for window sizing until ingest latency is
                  exposed.) */}
                  <TextInput
                    disabled={busy}
                    help={
                      <HelpTip
                        docHref={docUrl("concepts/alert-rules")}
                        docLabel="Alert rules"
                        label="About the alert window"
                      >
                        {"Compare against the 7-day samples below."}
                      </HelpTip>
                    }
                    label="Window seconds"
                    max={86400}
                    min={1}
                    required
                    type="number"
                    value={windowSeconds}
                    onChange={(event) => {
                      setWindowSeconds(event.currentTarget.value);
                    }}
                  />
                  <TextInput
                    disabled={busy}
                    label="Minimum occurrences"
                    min={1}
                    required
                    type="number"
                    value={minOccurrences}
                    onChange={(event) => {
                      setMinOccurrences(event.currentTarget.value);
                    }}
                  />
                  <TextInput
                    disabled={busy}
                    label="Consecutive breaches"
                    max={10}
                    min={1}
                    required
                    type="number"
                    value={consecutiveBreaches}
                    onChange={(event) => {
                      setConsecutiveBreaches(event.currentTarget.value);
                    }}
                  />
                  <TextInput
                    disabled={busy}
                    label="Resolve after clear"
                    min={1}
                    required
                    type="number"
                    value={resolveAfterClear}
                    onChange={(event) => {
                      setResolveAfterClear(event.currentTarget.value);
                    }}
                  />
                  <Select
                    disabled={busy}
                    label="No-data policy"
                    value={noDataPolicy}
                    onChange={(event) => {
                      markDirty();
                      setNoDataPolicy(
                        event.currentTarget.value as AlertNoDataPolicy,
                      );
                    }}
                  >
                    <option value="keep">Keep current state</option>
                    <option value="resolve">Resolve</option>
                    <option value="notify">Notify</option>
                  </Select>
                </Grid>
              </>
            ) : gateSupported ? (
              <>
                <Select
                  disabled={busy}
                  label="Quality gate"
                  value={gateEnabled ? "pass_rate" : "none"}
                  onChange={(event) => {
                    markDirty();
                    setGateEnabled(event.currentTarget.value === "pass_rate");
                  }}
                >
                  <option value="none">Any completed batch</option>
                  <option value="pass_rate">Pass-rate gate</option>
                </Select>
                {gateEnabled ? (
                  <Grid columns={2} gap={4}>
                    <Select
                      disabled={busy}
                      label="Gate comparator"
                      value={gateComparator}
                      onChange={(event) => {
                        markDirty();
                        setGateComparator(
                          event.currentTarget.value as AlertComparator,
                        );
                      }}
                    >
                      <option value="gt">Greater than</option>
                      <option value="gte">Greater than or equal</option>
                      <option value="lt">Less than</option>
                      <option value="lte">Less than or equal</option>
                      <option value="eq">Equal</option>
                    </Select>
                    <TextInput
                      disabled={busy}
                      label="Gate threshold"
                      required
                      step="any"
                      type="number"
                      value={gateThreshold}
                      onChange={(event) => {
                        setGateThreshold(event.currentTarget.value);
                      }}
                    />
                  </Grid>
                ) : null}
              </>
            ) : null}
            {onPreview ? (
              <AlertRuleBacktest
                errorMessage={previewErrorMessage}
                isPending={isPreviewPending}
                onRun={() => {
                  void onPreview({
                    time_range: timeRangeForRecentHistory(),
                    trigger: buildTrigger(),
                  }).catch(() => undefined);
                }}
                preview={preview}
              />
            ) : null}
          </Stack>
        </Section>

        <Section
          hairline
          heading="Decide who to notify"
          description="No-data events use the firing channels."
        >
          <Stack gap={4}>
            <Grid columns={2} gap={4}>
              <MultiSelect
                description="Choose up to two channels."
                disabled={
                  busy || isOptionsPending || optionsErrorMessage != null
                }
                emptyLabel="Create an alert channel before saving this rule."
                label={rule ? "Firing channels" : `${severity} · firing`}
                onChange={(next) => {
                  markDirty();
                  setFiringChannelIds(next);
                }}
                options={alertChannelOptionsForSelection(
                  channelOptions,
                  firingChannelIds,
                )}
                value={firingChannelIds}
              />
              <MultiSelect
                description="Choose up to two channels."
                disabled={
                  busy || isOptionsPending || optionsErrorMessage != null
                }
                emptyLabel="Create an alert channel before saving this rule."
                label={rule ? "Resolved channels" : `${severity} · resolved`}
                onChange={(next) => {
                  markDirty();
                  setResolvedChannelIds(next);
                }}
                options={alertChannelOptionsForSelection(
                  channelOptions,
                  resolvedChannelIds,
                )}
                value={resolvedChannelIds}
              />
            </Grid>
            <Text variant="muted">No data · {severity} → firing channels</Text>
            {triggerKind === "threshold" ? (
              <TextInput
                description="0 disables repeat notifications."
                disabled={busy}
                label="Renotify seconds"
                min={0}
                required
                type="number"
                value={renotifySeconds}
                onChange={(event) => {
                  setRenotifySeconds(event.currentTarget.value);
                }}
              />
            ) : null}
            {optionsErrorMessage != null && onRetryOptions ? (
              <Inline>
                <Button
                  onClick={onRetryOptions}
                  type="button"
                  variant="secondary"
                >
                  Retry channels
                </Button>
              </Inline>
            ) : null}
          </Stack>
        </Section>

        {catalog ? (
          <Section hairline heading="Say what happened">
            <AlertRuleMessageEditor
              catalog={catalog}
              config={messageConfig}
              disabled={busy}
              onChange={(next) => {
                markDirty();
                setMessageConfig(next);
              }}
              triggerKind={triggerKind}
            />
          </Section>
        ) : null}
      </Stack>
      {actionsPlacement === "footer" ? (
        <Inline className="marker-alert-rule-authoring__footer" justify="end">
          {actions}
        </Inline>
      ) : null}
    </form>
  );
}
